// redux/slices/giftCertificatesSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl } from "../../Utils/serverSelector";
import { getJwtToken } from "../../Utils/authStorage";

const axiosRequest = async ({ url, method = "GET", data }) => {
  const jwt = await getJwtToken();

  const config = {
    url: `${getServerUrl()}${url}`,
    method,
    headers: {
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
  };

  if (data !== undefined) {
    config.data = data;
    config.headers["Content-Type"] = "application/json";
  }

  return axios(config);
};

const errMsg = (e, fallback) =>
  e?.response?.data?.error ||
  e?.response?.data?.message ||
  e?.message ||
  fallback ||
  "Request failed";

export const fetchGiftCertificates = createAsyncThunk(
  "giftCertificates/fetch",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      const res = await axiosRequest({ url: `/gift-certificates/${uid}` });
      return res.data;
    } catch (e) {
      return rejectWithValue(e?.response?.data || errMsg(e, "Fetch GiftCertificates failed"));
    }
  }
);

export const createGiftCertificate = createAsyncThunk(
  "giftCertificates/create",
  async ({ uid, certData }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      const res = await axiosRequest({
        url: "/gift-certificates",
        method: "POST",
        data: { uid, ...certData },
      });
      return res.data?.certificate || res.data;
    } catch (e) {
      return rejectWithValue(e?.response?.data || errMsg(e, "Create GiftCertificate failed"));
    }
  }
);

export const checkGiftCertificate = createAsyncThunk(
  "giftCertificates/check",
  async ({ code }, { rejectWithValue }) => {
    try {
      if (!code) throw new Error("Код сертификата не передан");
      const res = await axiosRequest({
        url: `/gift-certificates/check/${encodeURIComponent(code)}`,
      });
      return res.data?.certificate || res.data;
    } catch (e) {
      return rejectWithValue(e?.response?.data || errMsg(e, "Check GiftCertificate failed"));
    }
  }
);

export const applyGiftCertificate = createAsyncThunk(
  "giftCertificates/apply",
  async ({ code, amountToDeduct, appointmentId, clientId }, { rejectWithValue }) => {
    try {
      if (!code) throw new Error("Код сертификата не передан");

      const res = await axiosRequest({
        url: "/gift-certificates/apply",
        method: "POST",
        data: { code, amountToDeduct, appointmentId, clientId },
      });

      return res.data?.certificate || res.data;
    } catch (e) {
      return rejectWithValue(e?.response?.data || errMsg(e, "Apply GiftCertificate failed"));
    }
  }
);

export const cancelGiftCertificate = createAsyncThunk(
  "giftCertificates/cancel",
  async ({ id }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID сертификата не передан");

      const res = await axiosRequest({
        url: `/gift-certificates/${id}/cancel`,
        method: "PUT",
        data: {},
      });

      return res.data?.certificate || res.data;
    } catch (e) {
      console.error("❌ Cancel GiftCertificate:", {
        id,
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
      });

      return rejectWithValue(
        e?.response?.data ||
        errMsg(e, "Cancel GiftCertificate failed")
      );
    }
  }
);

export const deleteGiftCertificate = createAsyncThunk(
  "giftCertificates/delete",
  async ({ id }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID сертификата не передан");

      const res = await axiosRequest({
        url: `/gift-certificates/${id}`,
        method: "DELETE",
        data: {},
      });

      return id;
    } catch (e) {
      console.error("❌ Delete GiftCertificate:", {
        id,
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
      });

      return rejectWithValue(
        e?.response?.data ||
        errMsg(e, "Delete GiftCertificate failed")
      );
    }
  }
);

const initialState = {
  certificates: [],
  checkedCert: null,
  status: "idle",
  error: null,
};

const giftCertificatesSlice = createSlice({
  name: "giftCertificates",
  initialState,
  reducers: {
    patchCertificateLocal: (state, action) => {
      const { id, changes } = action.payload || {};
      const index = state.certificates.findIndex(
        (cert) => String(cert.id) === String(id)
      );
      if (index !== -1) {
        state.certificates[index] = {
          ...state.certificates[index],
          ...changes,
        };
      }
    },
    clearCheckedCert: (state) => {
      state.checkedCert = null;
    },
    clearGiftCertificatesError: (state) => {
      state.error = null;
    },
    resetGiftCertificatesStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },
    clearGiftCertificates: (state) => {
      state.certificates = [];
      state.checkedCert = null;
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGiftCertificates.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchGiftCertificates.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.certificates = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchGiftCertificates.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(createGiftCertificate.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createGiftCertificate.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload) state.certificates.unshift(action.payload);
      })
      .addCase(createGiftCertificate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(checkGiftCertificate.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.checkedCert = null;
      })
      .addCase(checkGiftCertificate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.checkedCert = action.payload || null;
      })
      .addCase(checkGiftCertificate.rejected, (state, action) => {
        state.status = "failed";
        state.checkedCert = null;
        state.error = action.payload || action.error?.message;
      })

      .addCase(applyGiftCertificate.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(applyGiftCertificate.fulfilled, (state, action) => {
        state.status = "succeeded";
        const updated = action.payload;
        if (!updated?.id) return;

        const index = state.certificates.findIndex(
          (cert) => String(cert.id) === String(updated.id)
        );

        if (index !== -1) state.certificates[index] = updated;

        if (String(state.checkedCert?.id) === String(updated.id)) {
          state.checkedCert = updated;
        }
      })
      .addCase(applyGiftCertificate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(cancelGiftCertificate.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(cancelGiftCertificate.fulfilled, (state, action) => {
        state.status = "succeeded";
        const updated = action.payload;
        if (!updated?.id) return;

        const index = state.certificates.findIndex(
          (cert) => String(cert.id) === String(updated.id)
        );

        if (index !== -1) state.certificates[index] = updated;
      })
      .addCase(cancelGiftCertificate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(deleteGiftCertificate.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteGiftCertificate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.certificates = state.certificates.filter(
          (cert) => String(cert.id) !== String(action.payload)
        );
      })
      .addCase(deleteGiftCertificate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(resetStore, () => initialState);
  },
});

export const {
  patchCertificateLocal,
  clearCheckedCert,
  clearGiftCertificatesError,
  resetGiftCertificatesStatus,
  clearGiftCertificates,
} = giftCertificatesSlice.actions;

export const selectGiftCertificates = (state) =>
  state.giftCertificates?.certificates || [];

export const selectCheckedGiftCertificate = (state) =>
  state.giftCertificates?.checkedCert || null;

export const selectGiftCertificatesStatus = (state) =>
  state.giftCertificates?.status || "idle";

export const selectGiftCertificatesError = (state) =>
  state.giftCertificates?.error || null;

export default giftCertificatesSlice.reducer;